import { t } from '@hefe/i18n';
import { BasketballTeam, SelectOption } from '@hefe/models';
import {
  BasketballTeamUpdateTeamRequest,
  BasketballTeamsSlice,
} from '@hefe/store';
import { useState } from 'react';
import { useAppDispatch } from '../../../../store/store';
import CustomForm from '../../../core/components/form/custom-form';
import { formatDatabaseDate } from '@hefe/utils';

interface EditTeamFormProps {
  team: BasketballTeam;
  coaches: SelectOption[];
}

export default function EditTeamForm(props: EditTeamFormProps) {
  const { coaches, team } = props;
  const dispatch = useAppDispatch();

  const [isSaving, setSaving] = useState(false);

  const onSubmit = (values: any, resetForm: any) => {
    const syncUntil = values.sync_practies_until
      ? formatDatabaseDate(values.sync_practies_until)
      : undefined;

    const submitValues: BasketballTeamUpdateTeamRequest = {
      id: team.id,
      name: values.name,
      sex: values.sex,
      age_min: values.age_min,
      age_max: values.age_max,
      ...(syncUntil ? { sync_practices_until: syncUntil } : {}),
      coaches: values.coaches.map((coach: number) => {
        return {
          id: coach,
        };
      }),
    };

    setSaving(true);
    dispatch(BasketballTeamsSlice.updateBasketballTeam(submitValues))
      .unwrap()
      .catch(() => null)
      .finally(() => setSaving(false));
  };

  return (
    <CustomForm
      key={team.id}
      loading={isSaving}
      fields={[
        {
          name: 'name',
          label: t('name'),
          type: 'text',
          initalValue: team.name,
          rules: [
            {
              required: true,
              type: 'string',
              message: t('form_errors.name'),
            },
          ],
        },
        {
          name: 'sex',
          type: 'radiogroup',
          label: t('sex.title'),
          placeholder: t('sex.title'),
          initalValue: team.sex,
          selectOptions: [
            { label: t('sex.male'), value: 1 },
            { label: t('sex.female'), value: 2 },
            { label: t('sex.diverse'), value: 3 },
          ],
          rules: [
            {
              type: 'number',
              message: t('form_errors.sex'),
            },
          ],
        },
        {
          name: 'age_min',
          label: t('age.min'),
          type: 'number',
          initalValue: team.age_min,
          rules: [
            {
              type: 'number',
              min: 1,
              message: t('form_errors.number_min', {
                min: 1,
              }),
            },
            {
              type: 'number',
              max: 255,
              message: t('form_errors.number_max', {
                max: 255,
              }),
            },
          ],
        },
        {
          name: 'age_max',
          label: t('age.max'),
          type: 'number',
          initalValue: team.age_max,
          extraLabel: t('age.team_settings.info'),
          rules: [
            {
              type: 'number',
              min: 1,
              message: t('form_errors.number_min', {
                min: 1,
              }),
            },
            {
              type: 'number',
              max: 255,
              message: t('form_errors.number_max', {
                max: 255,
              }),
            },
          ],
        },
        {
          name: 'sync_practies_until',
          label: t('practices.sync.until'),
          type: 'date',
          initalValue: team.sync_practices_until,
          rules: [
            {
              type: 'date',
              message: t('form_errors.date'),
            },
          ],
        },
        {
          name: 'coaches',
          label: t('coaches'),
          type: 'select',
          initalValue: team.coaches?.map((coach) => coach.id),
          multiSelect: true,
          rules: [],
          selectOptions: coaches,
        },
      ]}
      onSubmit={onSubmit}
    />
  );
}
